import { ArrowRightIcon, ClockIcon } from "@phosphor-icons/react/dist/ssr";
import type { ComponentType } from "react";
import { Link } from "zudoku/components";
import { cn } from "zudoku/ui/util";

interface PickerCardProps {
  href: string;
  icon: ComponentType<{ size?: number }>;
  label: string;
  desc: string;
  time?: string;
  needs?: string;
  cta: string;
  featured?: boolean;
  color?: string;
  onIntent?: () => void;
}

/** Real links keep every tutorial choice usable with a keyboard or without JS. */
export function PickerCard({
  href,
  icon: Icon,
  label,
  desc,
  time,
  needs,
  cta,
  featured,
  color,
  onIntent,
}: PickerCardProps) {
  return (
    <Link
      to={href}
      viewTransition
      onMouseEnter={onIntent}
      onFocus={onIntent}
      onTouchStart={onIntent}
      className="group relative flex min-w-0 flex-col gap-4 rounded-xl border border-border bg-card p-5 no-underline transition-[background-color,border-color,box-shadow] hover:border-foreground/25 hover:bg-muted/20 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring sm:p-6"
    >
      <span className="flex items-center justify-between gap-3">
        <span
          aria-hidden="true"
          className={cn(
            "inline-flex size-11 shrink-0 items-center justify-center rounded-xl",
            color ?? "bg-muted text-foreground",
          )}
        >
          <Icon size={24} />
        </span>
        {featured && (
          <span className="rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground">
            Featured tutorial
          </span>
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            "text-lg font-semibold leading-snug tracking-tight text-foreground",
            needs && "lg:min-h-12",
          )}
        >
          {label}
        </span>
        <span className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {desc}
        </span>
        {needs && (
          <span className="mt-auto pt-6 text-xs leading-relaxed text-muted-foreground">
            <span className="mb-1 block font-medium text-foreground">
              What you'll need
            </span>
            {needs}
          </span>
        )}
        <span
          className={cn(
            "flex flex-wrap items-center justify-between gap-3 text-sm",
            needs ? "mt-5" : "mt-auto pt-6",
          )}
        >
          <span
            className={cn(
              "inline-flex items-center gap-2 font-medium text-foreground",
              needs &&
                "w-full justify-between rounded-lg border border-border bg-muted/40 px-3 py-2.5 transition-colors group-hover:bg-foreground group-hover:text-background group-focus-visible:bg-foreground group-focus-visible:text-background",
            )}
          >
            {cta}
            <ArrowRightIcon
              aria-hidden="true"
              size={16}
              className="transition-transform motion-safe:group-hover:translate-x-1"
            />
          </span>
          {time && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <ClockIcon aria-hidden="true" size={14} />
              {time}
            </span>
          )}
        </span>
      </span>
    </Link>
  );
}
